import { baseApi } from './baseApi';
import { withDefinedFields } from './apiUtils';
import { setSubscriberCredentials } from '../slices/subscriberAuthSlice';

export const subscribeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPublicGroups: builder.query({
      query: (organizationSlug) => `/public/organizations/${organizationSlug}/groups`,
      providesTags: (result, error, organizationSlug) => [
        { type: 'PublicGroup', id: organizationSlug },
      ],
    }),
    requestCode: builder.mutation({
      query: ({ organizationSlug, email }) => ({
        url: `/public/organizations/${organizationSlug}/subscribe/request-code`,
        method: 'POST',
        body: { email },
      }),
    }),
    verifyCode: builder.mutation({
      query: ({ organizationSlug, subscriberId, code }) => ({
        url: `/public/organizations/${organizationSlug}/subscribe/verify`,
        method: 'POST',
        body: { subscriber_id: subscriberId, code },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled.catch(() => ({}));
        if (data?.token) dispatch(setSubscriberCredentials({ token: data.token }));
      },
    }),
    getSubscriberSubscriptions: builder.query({
      query: ({ organizationSlug, subscriberId }) =>
        `/public/organizations/${organizationSlug}/subscribers/${subscriberId}/subscriptions`,
    }),
    getMyGroups: builder.query({
      query: (organizationSlug) => `/public/organizations/${organizationSlug}/me`,
      providesTags: ['Subscriber'],
    }),
    subscribe: builder.mutation({
      query: ({ organizationSlug, subscriberId, groupId, channel, latitude, longitude }) => ({
        url: `/public/organizations/${organizationSlug}/subscribe`,
        method: 'POST',
        body: {
          subscriber_id: subscriberId,
          group_id: groupId,
          channel,
          terms_accepted: true,
          latitude,
          longitude,
        },
      }),
      invalidatesTags: ['Subscriber'],
    }),
    unsubscribe: builder.mutation({
      query: ({ organizationSlug, subscriberId, groupId, channel }) => ({
        url: `/public/organizations/${organizationSlug}/unsubscribe`,
        method: 'POST',
        body: {
          subscriber_id: subscriberId,
          group_id: groupId,
          channel,
        },
      }),
      invalidatesTags: ['Subscriber'],
    }),
    setSubscriptionActive: builder.mutation({
      query: ({ organizationSlug, subscriptionId, isActive }) => ({
        url: `/public/organizations/${organizationSlug}/subscriptions/${subscriptionId}/active`,
        method: 'PATCH',
        body: { is_active: isActive },
      }),
      invalidatesTags: ['Subscriber'],
    }),
    pauseSubscription: builder.mutation({
      query: ({ organizationSlug, subscriptionId, pausedUntil }) => ({
        url: `/public/organizations/${organizationSlug}/subscriptions/${subscriptionId}/pause`,
        method: 'PATCH',
        body: { paused_until: pausedUntil },
      }),
      invalidatesTags: ['Subscriber'],
    }),
    updateMe: builder.mutation({
      query: ({ organizationSlug, phone, enabledChannels, telegramChatId }) => ({
        url: `/public/organizations/${organizationSlug}/me`,
        method: 'PATCH',
        body: withDefinedFields([
          ['phone', phone],
          ['enabled_channels', enabledChannels],
          ['telegram_chat_id', telegramChatId],
        ]),
      }),
      invalidatesTags: ['Subscriber'],
    }),
    startTelegramLink: builder.mutation({
      query: ({ organizationSlug }) => ({
        url: `/public/organizations/${organizationSlug}/telegram/link-start`,
        method: 'POST',
      }),
    }),
    // getPushPublicKey: builder.query({
    //   query: () => '/public/push-public-key',
    // }),
    // addPushSubscription: builder.mutation({
    //   query: ({ organizationSlug, endpoint, keys }) => ({
    //     url: `/public/organizations/${organizationSlug}/push-subscriptions`,
    //     method: 'POST',
    //     body: { endpoint, keys },
    //   }),
    // }),
    // removePushSubscription: builder.mutation({
    //   query: ({ organizationSlug, endpoint }) => ({
    //     url: `/public/organizations/${organizationSlug}/push-subscriptions?endpoint=${encodeURIComponent(endpoint)}`,
    //     method: 'DELETE',
    //   }),
    // }),
    startGroupTelegramLink: builder.mutation({
      query: ({ organizationSlug, groupId }) => ({
        url: `/public/organizations/${organizationSlug}/groups/${groupId}/telegram/chat-link`,
        method: 'POST',
      }),
    }),
    unsubscribeByToken: builder.query({
      query: ({ token, groupId, channel }) => ({
        url: `/public/unsubscribe/${token}`,
        params: withDefinedFields([
          ['group_id', groupId],
          ['channel', channel],
        ]),
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPublicGroupsQuery,
  useRequestCodeMutation,
  useVerifyCodeMutation,
  useGetSubscriberSubscriptionsQuery,
  useLazyGetSubscriberSubscriptionsQuery,
  useGetMyGroupsQuery,
  useSubscribeMutation,
  useUnsubscribeMutation,
  useSetSubscriptionActiveMutation,
  usePauseSubscriptionMutation,
  useUpdateMeMutation,
  useStartTelegramLinkMutation,
  useStartGroupTelegramLinkMutation,
  useUnsubscribeByTokenQuery,
  // useLazyGetPushPublicKeyQuery,
  // useAddPushSubscriptionMutation,
  // useRemovePushSubscriptionMutation,
} = subscribeApi;
